import React, { useEffect, useMemo } from 'react';
import { useStore } from 'components/Store';
import DetailPanel from 'screens/Applications&WorkOrders/Components/DetailPanel';
import DetailOverview from 'screens/Applications&WorkOrders/Components/DetailOverview';
import DetailsTabs from 'screens/Applications&WorkOrders/Components/DetailsTabs';
import useOperationProducts from 'screens/Applications&WorkOrders/Components/hooks/useOperationProducts';
import useOperationProperties from 'screens/Applications&WorkOrders/Components/hooks/useOperationProperties';
import { getTargetPests } from 'screens/Applications&WorkOrders/Components/helpers';
import ActionMenu from 'screens/Applications&WorkOrders/Components/ActionMenu';
import { OperationType } from 'screens/Applications&WorkOrders/RouteAdmin';
import useApplicationDetails from 'screens/Applications&WorkOrders/Applications/Hooks/useApplicationDetails';
import useWorkOrderDetails from 'screens/Applications&WorkOrders/WorkOrders/Hooks/useWorkOrderDetails';
import { useSummaryParams } from 'screens/Applications&WorkOrders/Context/SummaryParamsContext';
import { OperationDetails } from 'screens/Applications&WorkOrders/Components/types';

export interface OperationDetailPanelProps {
  operationId: string | null;
  operationType: OperationType;
  isLoading: boolean;
  onOperationDeleted?: (deletedId: string) => void;
  hasOperations: boolean;
}

const OperationDetailPanel = ({
  operationId,
  operationType,
  isLoading,
  onOperationDeleted,
  hasOperations
}: OperationDetailPanelProps) => {
  const {
    state: { organization }
  } = useStore();
  const { hasSearchTerm, hasFilters } = useSummaryParams();

  /*
    Pass null when not needed to avoid unnecessary API calls,
    Prevent fetching details when the operation summary is still loading
  */
  const applicationDetailsResult = useApplicationDetails(
    operationType === OperationType.APPLICATION && !isLoading
      ? operationId
      : null
  );
  const workOrderDetailsResult = useWorkOrderDetails(
    operationType === OperationType.WORK_ORDER && !isLoading
      ? operationId
      : null
  );

  // Get the appropriate details based on operation type
  const operationDetails: OperationDetails | undefined =
    operationType === OperationType.APPLICATION
      ? applicationDetailsResult.applicationDetails
      : workOrderDetailsResult.workOrderDetails;

  const isLoadingDetails =
    operationType === OperationType.APPLICATION
      ? applicationDetailsResult.isLoading
      : workOrderDetailsResult.isLoading;

  const detailsError =
    operationType === OperationType.APPLICATION
      ? applicationDetailsResult.errors
      : workOrderDetailsResult.errors;

  const refetch =
    operationType === OperationType.APPLICATION
      ? applicationDetailsResult.refetch
      : workOrderDetailsResult.refetch;

  useEffect(() => {
    if (operationId) {
      refetch();
    }
  }, [operationId, refetch]);

  // Only fetch products/properties if the operation belongs to the current organization
  const isOperationFromCurrentOrg =
    operationDetails?.orgId === organization?.id;

  const operationIdForHooks = isOperationFromCurrentOrg
    ? operationDetails?.id
    : null;

  const operationTypeString =
    operationType === OperationType.APPLICATION ? 'application' : 'work-order';

  const { products, isLoading: isLoadingProducts } = useOperationProducts(
    operationIdForHooks,
    operationTypeString
  );

  const {
    propertiesData,
    isLoading: isLoadingProperties
  } = useOperationProperties(operationIdForHooks, operationTypeString);

  const operationDetailsWithTargetPests = useMemo(() => {
    if (!operationDetails) return operationDetails;

    // The same productId can appear multiple times in operationDetails (e.g. split
    // across crop zones), so we group all entries by productId into ordered lists.
    const productDetailsByProductId = new Map<
      string,
      NonNullable<typeof operationDetails.products>[number][]
    >();
    operationDetails.products?.forEach(product => {
      const list = productDetailsByProductId.get(product.productId) ?? [];
      list.push(product);
      productDetailsByProductId.set(product.productId, list);
    });

    const consumedIndices = new Map<string, number>();

    return {
      ...operationDetails,
      products: products?.map(product => {
        const list = productDetailsByProductId.get(product.productId) ?? [];
        // Each time we encounter a productId we advance its pointer so that
        // repeated entries in `products` consume distinct detail records in order.
        const index = consumedIndices.get(product.productId) ?? 0;
        // Fall back to list[0] if there are fewer detail records than product rows.
        const productDetail = list[index] ?? list[0];
        consumedIndices.set(product.productId, index + 1);

        return {
          ...productDetail,
          ...product,
          targetPests: getTargetPests(product, products)
        };
      })
    };
  }, [operationDetails, products]);

  const testId =
    operationType === OperationType.APPLICATION
      ? 'application-detail-panel'
      : 'work-order-detail-panel';

  const showWeatherTab = operationType === OperationType.APPLICATION;

  const isWaitingForDetails =
    !!operationId && operationId !== operationDetails?.id && !detailsError;

  return (
    <DetailPanel
      title={operationDetails?.name}
      isLoading={isLoading || isLoadingDetails || isWaitingForDetails}
      isEmpty={!isWaitingForDetails && !operationDetails?.id}
      showSearchEmptyState={hasSearchTerm && !hasOperations}
      showFilterEmptyState={hasFilters && !hasOperations}
      operationType={operationType}
      testId={testId}
      actionMenu={
        <ActionMenu
          operation={operationDetailsWithTargetPests}
          operationType={operationType}
          onDelete={onOperationDeleted}
        />
      }
    >
      <DetailOverview details={operationDetails} />
      <DetailsTabs
        details={operationDetails}
        products={products}
        propertiesData={propertiesData}
        tankInformation={operationDetails?.tankInformation}
        isLoadingProducts={isLoadingProducts}
        isLoadingProperties={isLoadingProperties}
        showWeatherTab={showWeatherTab}
        type={
          operationType === OperationType.WORK_ORDER
            ? OperationType.WORK_ORDER
            : undefined
        }
      />
    </DetailPanel>
  );
};

export default OperationDetailPanel;
